import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, Input, Modal, Popconfirm, Select, Space, Table } from 'antd';
import { apiClient, useAuth } from '@isocel/front-common';

import { useNotification } from '../notification/Notification';
import { fetchStores } from '../../queries/api/store';
import './StoreUsers.less';
interface StoreUser {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
}

interface StoreUsersResponse {
    items: StoreUser[];
    totalCount: number;
    page?: number;
    pageSize?: number;
}
interface CreateUserFormValues {
    firstName: string;
    lastName: string;
    email: string;
    permission: 'storeUser' | 'storeAdmin';
    store?: string;
}

const StoreUsers: React.FC = () => {
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [users, setUsers] = useState<StoreUser[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [form] = Form.useForm<CreateUserFormValues>();
    const permission = [
        { label: 'Utilisateur Magasin', value: 'storeUser' },
        { label: 'Administrateur Magasin', value: 'storeAdmin' },
    ];
    const { showNotification } = useNotification();
    const { user } = useAuth();
    const showNotificationRef = useRef(showNotification);

    const organizationId = user?.organization?.id ?? '';
    const [stores, setStores] = useState<any[]>([]);
    const storeId = user && (user as any).store;

    useEffect(() => {
        const fetchStoreDetails = async () => {
            if (storeId) {
                const storesList = await fetchStores();
                const store = storesList.find((s: any) => s.id === storeId);
                if (store) {
                    setStores([store]);
                }
            } else {
                const storesList = await fetchStores();
                setStores(storesList);
            }
        };
        fetchStoreDetails();
    }, [storeId, organizationId]);

    useEffect(() => {
        showNotificationRef.current = showNotification;
    }, [showNotification]);

    const fetchStoreUsers = useCallback(
        async (page: number, size: number) => {
            if (!organizationId) {
                setUsers([]);
                setTotalCount(0);
                return;
            }

            setIsLoading(true);
            try {
                const { data } = await apiClient().get<StoreUsersResponse>(`/users/store-users`, {
                    params: {
                        page: page - 1,
                        pageSize: size,
                        sortOrder: 'desc',
                        search: '',
                    },
                });
                setUsers(data.items ?? []);
                setTotalCount(data.totalCount ?? 0);
                setCurrentPage(page);
                setPageSize(size);
            } catch (error) {
                showNotificationRef.current('error', 'Erreur', 'Impossible de charger les utilisateurs.');
            } finally {
                setIsLoading(false);
            }
        },
        [organizationId]
    );

    useEffect(() => {
        fetchStoreUsers(1, pageSize);
    }, [fetchStoreUsers, organizationId, pageSize]);

    const tableData = useMemo(
        () =>
            users.map((storeUser) => ({
                key: storeUser.id,
                ...storeUser,
            })),
        [users]
    );

    const openCreateModal = () => {
        form.resetFields();
        setIsCreateModalOpen(true);
    };

    const closeCreateModal = () => {
        if (!isSubmitting) {
            setIsCreateModalOpen(false);
        }
    };

    const handleCreateUser = async (values: CreateUserFormValues) => {
        if (!organizationId) {
            showNotification('error', 'Erreur', 'Aucune organisation connectée.');
            return;
        }

        try {
            setIsSubmitting(true);
            await apiClient().post('/users/store-users', {
                ...values,
                organization: organizationId,
                store: values.store || undefined,
            });
            showNotification('success', 'Succès', 'Utilisateur créé avec succès.');
            setIsCreateModalOpen(false);
            await fetchStoreUsers(currentPage, pageSize);
        } catch (error) {
            showNotification('error', 'Erreur', "Impossible de créer l'utilisateur.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleResetPassword = async (storeUser: StoreUser) => {
        try {
            await apiClient().post(`/users/${storeUser.id}/reset-password`);
            showNotification(
                'success',
                'Mot de passe réinitialisé',
                `Le mot de passe de ${storeUser.firstName || ''} ${storeUser.lastName || ''} a été réinitialisé.`
            );
        } catch (error) {
            showNotification('error', 'Erreur', 'Impossible de réinitialiser le mot de passe.');
        }
    };

    const handleDeleteUser = async (storeUser: StoreUser) => {
        try {
            await apiClient().delete(`/users/${storeUser.id}`);
            showNotification('success', 'Succès', 'Compte supprimé avec succès.');
            await fetchStoreUsers(currentPage, pageSize);
        } catch (error) {
            showNotification('error', 'Erreur', 'Impossible de supprimer le compte.');
        }
    };

    const handleTablePaginationChange = async (page: number, size?: number) => {
        await fetchStoreUsers(page, size ?? pageSize);
    };

    return (
        <div className="store-users-page">
            <h1>Utilisateurs magasins</h1>
            <span className="store-users-subHeader">Mot de passe par défaut: &quot;Password@123&quot;</span>
            <div className="store-users-actions">
                <Button type="primary" onClick={openCreateModal}>
                    Créer un utilisateur
                </Button>
            </div>
            <div className="table-container">
                <Table
                    rowKey="id"
                    size="small"
                    loading={isLoading}
                    dataSource={tableData}
                    pagination={{
                        current: currentPage,
                        pageSize,
                        total: totalCount,
                        showSizeChanger: true,
                        onChange: (page, size) => {
                            handleTablePaginationChange(page, size);
                        },
                    }}
                    columns={[
                        {
                            title: 'Prénom',
                            dataIndex: 'firstName',
                            key: 'firstName',
                        },
                        {
                            title: 'Nom',
                            dataIndex: 'lastName',
                            key: 'lastName',
                        },
                        {
                            title: "Nom d'utilisateur",
                            key: 'email',
                            render: (_, record: StoreUser) => record.email || '-',
                        },
                        {
                            title: 'Actions',
                            key: 'actions',
                            render: (_, record: StoreUser) => (
                                <Space size="small">
                                    <Button size="small" onClick={async () => await handleResetPassword(record)}>
                                        Réinitialiser le mot de passe
                                    </Button>
                                    <Popconfirm
                                        title="Supprimer ce compte ?"
                                        okText="Oui"
                                        cancelText="Non"
                                        onConfirm={async () => await handleDeleteUser(record)}
                                    >
                                        <Button size="small" danger>
                                            Supprimer l&apos;utilisateur
                                        </Button>
                                    </Popconfirm>
                                </Space>
                            ),
                        },
                    ]}
                />
            </div>

            <Modal
                title="Créer un utilisateur"
                visible={isCreateModalOpen}
                onCancel={closeCreateModal}
                okText="Créer"
                cancelText="Annuler"
                confirmLoading={isSubmitting}
                onOk={() => {
                    form.submit();
                }}
                destroyOnClose
            >
                <Form<CreateUserFormValues> form={form} layout="vertical" onFinish={handleCreateUser}>
                    <Form.Item
                        label="Prénom"
                        name="firstName"
                        rules={[{ required: true, message: 'Le prénom est requis.' }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item label="Nom" name="lastName" rules={[{ required: true, message: 'Le nom est requis.' }]}>
                        <Input />
                    </Form.Item>

                    <Form.Item
                        label="Nom d'utilisateur"
                        name="email"
                        rules={[{ required: true, message: "Le nom d'utilisateur est requis." }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        label="Rôle"
                        name="permission"
                        rules={[{ required: true, message: 'Le rôle est requis.' }]}
                    >
                        <Select style={{ width: '100%' }}>
                            {permission.map((r) => (
                                <Select.Option key={r.value} value={r.value}>
                                    {r.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item label="Magasin" name="store" rules={[{ required: false }]}>
                        <Select
                            showSearch
                            placeholder="Sélectionner un magasin"
                            optionFilterProp="children"
                            notFoundContent="Aucun résultat"
                            style={{ width: '100%' }}
                            disabled={!!storeId}
                        >
                            {stores.map((store: any) => (
                                <Select.Option key={store.id} value={store.id}>
                                    {store.code} - {store.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default StoreUsers;
